import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  CircularProgress,
  IconButton,
  InputAdornment,
  Snackbar,
  Alert,
  Switch,
  Divider,
  MenuItem,
} from '@mui/material';
import {
  Phone as PhoneIcon,
  Payment as PaymentIcon,
  AccountBalance as AccountBalanceIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Settings as SettingsIcon,
  HourglassEmpty as HourglassEmptyIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  ShoppingCart as ShoppingCartIcon,
  Chat as ChatIcon,
  ContentCopy as ContentCopyIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { apiService } from '../services/api';
import { MagicBackground } from '../components/MagicBackground';
import { Vendor } from '../types';
// Removed PhoneNumberSelection - all businesses now get dedicated numbers automatically

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA',
  'KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ',
  'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT',
  'VA','WA','WV','WI','WY','DC',
];

export default function Settings() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [vendor, setVendor] = useState<Vendor | null>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  // Phone settings - removed phoneNumberType, all businesses get dedicated numbers
  const [spendingData, setSpendingData] = useState<any>(null);
  const [spendingLimit, setSpendingLimit] = useState<number | null>(null);
  const [newSpendingLimit, setNewSpendingLimit] = useState<string>('');
  const [phoneStatus, setPhoneStatus] = useState<{
    phoneNumber: string | null;
    status: 'NOT_PROVISIONED' | 'PENDING' | 'ACTIVE' | 'UNKNOWN';
    numberType?: string;
    twoWayEnabled?: boolean;
    message: string;
  } | null>(null);

  // Square integration
  const [squareConnected, setSquareConnected] = useState(false);
  const [squareJustConnected, setSquareJustConnected] = useState(false);

  // Stripe integration
  const [stripePublishableKey, setStripePublishableKey] = useState('');
  const [stripeSecretKey, setStripeSecretKey] = useState('');
  const [showStripeSecret, setShowStripeSecret] = useState(false);
  const [stripeConnected, setStripeConnected] = useState(false);

  // Payout settings
  const [payoutMethod, setPayoutMethod] = useState<'stripe' | 'square' | 'bank'>('stripe');
  const [bankAccountNumber, setBankAccountNumber] = useState('');
  const [bankRoutingNumber, setBankRoutingNumber] = useState('');
  const [bankAccountName, setBankAccountName] = useState('');

  // Business Hours settings
  const [enforceBusinessHours, setEnforceBusinessHours] = useState(false);
  const [businessHours, setBusinessHours] = useState<Record<string, { open: string; close: string; isOpen: boolean }>>({});
  const [timezone, setTimezone] = useState('America/New_York');
  const [allowPreOrders, setAllowPreOrders] = useState(false);
  const [preOrderLeadTime, setPreOrderLeadTime] = useState(2);
  const [afterHoursMessage, setAfterHoursMessage] = useState('');

  // Upsell settings
  const [upsellEnabled, setUpsellEnabled] = useState(true);
  const [upsellStyle, setUpsellStyle] = useState<'natural' | 'aggressive' | 'none'>('natural');
  const [upsellMaxAttempts, setUpsellMaxAttempts] = useState(1);
  const [upsellSkipOnMultipleItems, setUpsellSkipOnMultipleItems] = useState(true);
  const [customUpsellMessage, setCustomUpsellMessage] = useState('');

  // Conversation settings
  const [staleTimeoutMinutes, setStaleTimeoutMinutes] = useState(30);
  const [resetOnNewDay, setResetOnNewDay] = useState(true);

  // Business type
  const [businessType, setBusinessType] = useState<'appointment' | 'order' | 'hybrid'>('order');
  
  // Business address (structured)
  const [addressStreet, setAddressStreet] = useState('');
  const [addressCity, setAddressCity] = useState('');
  const [addressState, setAddressState] = useState('');
  const [addressZip, setAddressZip] = useState('');
  
  // Employee PIN
  const [employeePin, setEmployeePin] = useState('');
  const [showEmployeePin, setShowEmployeePin] = useState(false);
  const [queueAccessUrl, setQueueAccessUrl] = useState('');

  useEffect(() => {
    // Handle OAuth callback from Square
    const params = new URLSearchParams(window.location.search);
    const squareStatus = params.get('square');
    
    if (squareStatus === 'connected') {
      setSnackbar({ 
        open: true, 
        message: 'Square connected successfully! You can now import your menu and accept payments.', 
        severity: 'success' 
      });
      setSquareConnected(true);
      setSquareJustConnected(true);
      // Clean URL first to prevent re-triggering
      window.history.replaceState({}, '', '/settings');
      // Reload settings after a delay to allow DynamoDB consistency and avoid auth race conditions
      setTimeout(() => {
        loadSettings().catch(err => {
          console.error('Error loading settings after Square connect:', err);
          // If loading fails, just set loading to false
          setLoading(false);
        });
      }, 1500);
    } else if (squareStatus === 'error') {
      const message = params.get('message') || 'Failed to connect Square';
      setSnackbar({ 
        open: true, 
        message: decodeURIComponent(message), 
        severity: 'error' 
      });
      // Clean URL
      window.history.replaceState({}, '', '/settings');
      loadSettings().catch(err => {
        console.error('Error loading settings:', err);
        setLoading(false);
      });
    } else {
      loadSettings().catch(err => {
        console.error('Error loading settings:', err);
        setLoading(false);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const vendorData = await apiService.getVendor();
      setVendor(vendorData);
      
      // Only load integrations if we have a vendor
      if (!vendorData || !vendorData.vendorId) {
        console.warn('No vendor selected - skipping integration loading');
        return;
      }
      
      // Load existing settings
      if (vendorData?.phone) {
        try {
          const spending = await apiService.getSMSSpending();
          setSpendingData(spending);
          setSpendingLimit(spending.spendingLimit);
        } catch (error: any) {
          console.error('Error loading spending data:', error);
          // Don't show error if it's a 403 - vendor might not have phone activated yet
          if (error.response?.status !== 403) {
            console.error('Error loading spending data:', error);
          }
        }

        // Load phone number status
        try {
          const status = await apiService.getPhoneStatus();
          setPhoneStatus(status);
        } catch (error: any) {
          console.error('Error loading phone status:', error);
          // Don't show error if it's a 403
          if (error.response?.status !== 403) {
            console.error('Error loading phone status:', error);
          }
        }
      }
      
      // Load Square/Stripe integration status from backend
      try {
        console.log('[SETTINGS] Loading Square integration config...');
        const squareConfig = await apiService.getIntegration('square');
        console.log('[SETTINGS] Square config loaded (raw):', squareConfig);
        console.log('[SETTINGS] Square config details:', {
          connected: squareConfig?.connected,
          hasLocationId: !!squareConfig?.locationId,
          locationId: squareConfig?.locationId,
          allKeys: squareConfig ? Object.keys(squareConfig) : [],
        });
        const isConnected = squareConfig?.connected === true;
        console.log('[SETTINGS] Setting squareConnected to:', isConnected);
        
        // If we just completed OAuth, keep the connected state even if backend hasn't updated yet
        if (squareJustConnected) {
          console.log('[SETTINGS] OAuth just completed, keeping connected state');
          setSquareConnected(true);
        } else {
          setSquareConnected(isConnected);
        }
      } catch (error: any) {
        console.error('[SETTINGS] Error loading Square config:', error);
        // If we just connected via OAuth, keep the connected state despite the error
        if (squareJustConnected) {
          console.log('[SETTINGS] OAuth just completed, keeping connected state despite error');
          setSquareConnected(true);
        }
        // Don't show error if it's a 403 - might be expected if vendor not set up
        if (error.response?.status !== 403) {
          console.error('Error loading Square config:', error);
        }
      }

      try {
        console.log('[SETTINGS] Loading Stripe integration config...');
        const stripeConfig = await apiService.getIntegration('stripe');
        console.log('[SETTINGS] Stripe config loaded (raw):', stripeConfig);
        console.log('[SETTINGS] Stripe config loaded:', {
          connected: stripeConfig?.connected,
          hasPublishableKey: !!stripeConfig?.publishableKey,
          publishableKeyLength: stripeConfig?.publishableKey?.length || 0,
          allKeys: stripeConfig ? Object.keys(stripeConfig) : [],
        });
        const isConnected = stripeConfig?.connected === true;
        console.log('[SETTINGS] Setting stripeConnected to:', isConnected);
        setStripeConnected(isConnected);
        if (isConnected && stripeConfig?.publishableKey) {
          console.log('[SETTINGS] Setting publishable key:', stripeConfig.publishableKey.substring(0, 10) + '...');
          setStripePublishableKey(stripeConfig.publishableKey);
        } else {
          console.log('[SETTINGS] Not setting publishable key - connected:', isConnected, 'hasKey:', !!stripeConfig?.publishableKey);
        }
      } catch (error: any) {
        console.log('[SETTINGS] Error loading Stripe config:', {
          status: error.response?.status,
          message: error.message,
          is403: error.response?.status === 403,
        });
        // Don't show error if it's a 403 - might be expected if vendor not set up
        if (error.response?.status !== 403) {
          console.error('Error loading Stripe config:', error);
        }
      }

      // Load vendor business hours, timezone, and business type
      if (vendorData) {
        // Load business type
        setBusinessType(vendorData.businessType || 'order');
        
        // Load business address — parse "Street, City, ST ZIP" if possible
        const rawAddr = vendorData.address || '';
        const addrParts = rawAddr.split(',').map((p: string) => p.trim());
        if (addrParts.length >= 3) {
          const lastPart = addrParts[addrParts.length - 1]; // "CO 80015" or "CO"
          const stateZip = lastPart.split(' ').filter(Boolean);
          setAddressStreet(addrParts.slice(0, addrParts.length - 2).join(', '));
          setAddressCity(addrParts[addrParts.length - 2]);
          setAddressState(stateZip[0] || '');
          setAddressZip(stateZip[1] || '');
        } else if (addrParts.length === 2) {
          setAddressStreet(addrParts[0]);
          setAddressCity(addrParts[1]);
        } else {
          setAddressStreet(rawAddr);
        }
        
        // Load employee PIN
        if (vendorData.employeePin) {
          setEmployeePin(vendorData.employeePin);
        }
        
        // Generate queue access URL (always set if we have vendorId)
        if (vendorData.vendorId) {
          setQueueAccessUrl(`${window.location.origin}/queue/${vendorData.vendorId}`);
        } else {
          // Clear if no vendorId
          setQueueAccessUrl('');
        }
        
        // Convert BusinessHours format to our state format
        const hours: Record<string, { open: string; close: string; isOpen: boolean }> = {};
        if (vendorData.businessHours) {
          const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
          days.forEach(day => {
            const daySchedule = vendorData.businessHours?.[day as keyof typeof vendorData.businessHours];
            if (daySchedule) {
              hours[day] = {
                open: daySchedule.open || '09:00',
                close: daySchedule.close || '17:00',
                isOpen: !daySchedule.closed,
              };
            }
          });
        }
        setBusinessHours(hours);
        setTimezone(vendorData.timezone || 'America/New_York');
      }

      // Load agent config for upsell, order, and conversation settings
      try {
        const agentConfig = await apiService.getAgentConfig();
        
        // Load upsell settings
        if (agentConfig && agentConfig.upsellSettings) {
          setUpsellEnabled(agentConfig.upsellSettings.enabled ?? true);
          setUpsellStyle(agentConfig.upsellSettings.style || 'natural');
          setUpsellMaxAttempts(agentConfig.upsellSettings.maxAttempts ?? 1);
          setUpsellSkipOnMultipleItems(agentConfig.upsellSettings.skipOnMultipleItems ?? true);
          setCustomUpsellMessage(agentConfig.upsellSettings.customUpsellMessage || '');
        }
        
        // Load order settings
        if (agentConfig && agentConfig.orderSettings) {
          setEnforceBusinessHours(agentConfig.orderSettings.enforceBusinessHours ?? false);
          setAllowPreOrders(agentConfig.orderSettings.allowPreOrders ?? false);
          setPreOrderLeadTime(agentConfig.orderSettings.preOrderLeadTime ?? 2);
          setAfterHoursMessage(agentConfig.orderSettings.afterHoursMessage || '');
        }
        
        // Load conversation settings
        if (agentConfig && agentConfig.conversationSettings) {
          setStaleTimeoutMinutes(agentConfig.conversationSettings.staleTimeoutMinutes ?? 30);
          setResetOnNewDay(agentConfig.conversationSettings.resetOnNewDay ?? true);
        }
      } catch (error: any) {
        console.error('Error loading agent config:', error);
      }

      // Load payout config
      try {
        const payoutConfig = await apiService.getPayoutConfig();
        setPayoutMethod(payoutConfig?.method || 'stripe');
        setBankAccountName(payoutConfig?.bankAccountName || '');
      } catch (error: any) {
        // Don't show error if it's a 403 - might be expected if vendor not set up
        if (error.response?.status !== 403) {
          console.error('Error loading payout config:', error);
        }
      }
    } catch (error) {
      console.error('Error loading settings:', error);
      setSnackbar({ open: true, message: 'Failed to load settings', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleActivatePhone = async () => {
    try {
      setSaving(true);
      const result = await apiService.activatePhoneNumber();
      setSnackbar({ open: true, message: result.message || 'Phone number activated successfully!', severity: 'success' });
      // Reload vendor data
      const vendorData = await apiService.getVendor();
      setVendor(vendorData);
      // Load spending data and phone status
      if (vendorData?.phone) {
        try {
          const spending = await apiService.getSMSSpending();
          setSpendingData(spending);
          setSpendingLimit(spending.spendingLimit);
        } catch (error) {
          console.error('Error loading spending data:', error);
        }
        try {
          const status = await apiService.getPhoneStatus();
          setPhoneStatus(status);
        } catch (error) {
          console.error('Error loading phone status:', error);
        }
      }
    } catch (error: any) {
      console.error('Error activating phone number:', error);
      setSnackbar({ open: true, message: error.message || 'Failed to activate phone number', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleSetSpendingLimit = async () => {
    try {
      setSaving(true);
      const limit = parseFloat(newSpendingLimit);
      if (isNaN(limit) || limit < 0) {
        setSnackbar({ open: true, message: 'Please enter a valid spending limit', severity: 'error' });
        return;
      }
      await apiService.setSpendingLimit(limit);
      setSpendingLimit(limit);
      setNewSpendingLimit('');
      setSnackbar({ open: true, message: 'Spending limit updated successfully!', severity: 'success' });
      // Reload spending data
      if (vendor?.phone) {
        try {
          const spending = await apiService.getSMSSpending();
          setSpendingData(spending);
        } catch (error) {
          console.error('Error loading spending data:', error);
        }
      }
    } catch (error: any) {
      console.error('Error setting spending limit:', error);
      setSnackbar({ open: true, message: error.message || 'Failed to set spending limit', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleConnectSquare = async () => {
    try {
      setSaving(true);
      console.log('[SETTINGS-SQUARE] Initiating Square OAuth flow...');
      
      // Get OAuth URL from backend
      const response = await apiService.initiateSquareOAuth();
      
      if (!response || !response.authUrl) {
        throw new Error('Invalid response from Square OAuth endpoint');
      }
      
      console.log('[SETTINGS-SQUARE] Got OAuth URL, redirecting to Square...');
      
      // Redirect to Square authorization page
      window.location.href = response.authUrl;
    } catch (error: any) {
      console.error('[SETTINGS-SQUARE] Error initiating Square OAuth:', error);
      setSnackbar({ 
        open: true, 
        message: error.message || 'Failed to connect Square. Please ensure you have selected a business.', 
        severity: 'error' 
      });
      setSaving(false);
    }
    // Don't set saving to false here - page will redirect
  };

  const handleConnectStripe = async () => {
    console.log('[SETTINGS-STRIPE] ========== CONNECT STRIPE CLICKED ==========');
    console.log('[SETTINGS-STRIPE] handleConnectStripe called');
    console.log('[SETTINGS-STRIPE] Current state:', {
      stripePublishableKey: stripePublishableKey ? `${stripePublishableKey.substring(0, 10)}...` : 'empty',
      stripePublishableKeyLength: stripePublishableKey.length,
      stripeSecretKey: stripeSecretKey ? `${stripeSecretKey.substring(0, 10)}...` : 'empty',
      stripeSecretKeyLength: stripeSecretKey.length,
      stripeConnected,
    });
    
    try {
      setSaving(true);
      const dataToSave = {
        publishableKey: stripePublishableKey,
        secretKey: stripeSecretKey,
      };
      
      console.log('[SETTINGS-STRIPE] Calling saveIntegration with:', {
        provider: 'stripe',
        hasPublishableKey: !!dataToSave.publishableKey,
        hasSecretKey: !!dataToSave.secretKey,
      });
      
      console.log('[SETTINGS-STRIPE] About to call saveIntegration...');
      const result = await apiService.saveIntegration('stripe', dataToSave);
      console.log('[SETTINGS-STRIPE] saveIntegration result (raw):', result);
      console.log('[SETTINGS-STRIPE] saveIntegration result type:', typeof result);
      console.log('[SETTINGS-STRIPE] saveIntegration result keys:', result ? Object.keys(result) : 'null/undefined');
      
      // Check if result has connected field
      const isConnected = result?.connected === true;
      console.log('[SETTINGS-STRIPE] Setting stripeConnected to:', isConnected);
      setStripeConnected(isConnected);
      
      setSnackbar({ open: true, message: 'Stripe connected successfully!', severity: 'success' });
      // Clear sensitive fields
      setStripeSecretKey('');
      console.log('[SETTINGS-STRIPE] Cleared secret key field');
      console.log('[SETTINGS-STRIPE] ========================================');
    } catch (error: any) {
      console.error('[SETTINGS-STRIPE] ERROR connecting Stripe:', error);
      console.error('[SETTINGS-STRIPE] Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });
      setSnackbar({ open: true, message: error.message || 'Failed to connect Stripe', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleSaveBusinessHours = async () => {
    try {
      setSaving(true);
      // Convert state format to BusinessHours format (with closed flag)
      const vendorHours: any = {};
      Object.keys(businessHours).forEach(day => {
        const dayHours = businessHours[day];
        vendorHours[day] = {
          open: dayHours.open,
          close: dayHours.close,
          closed: !dayHours.isOpen,
        };
      });
      
      // Save business hours to vendor
      if (vendor) {
        await apiService.updateVendor({
          businessHours: vendorHours,
          timezone,
        });
      }
      // Save order settings to agent config
      await apiService.saveAgentConfig({
        orderSettings: {
          enforceBusinessHours,
          afterHoursMessage: afterHoursMessage || undefined,
          allowPreOrders,
          preOrderLeadTime,
        },
      });
      setSnackbar({ open: true, message: 'Business hours saved successfully!', severity: 'success' });
    } catch (error: any) {
      console.error('Error saving business hours:', error);
      setSnackbar({ open: true, message: error.message || 'Failed to save business hours', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleSaveUpsellSettings = async () => {
    try {
      setSaving(true);
      await apiService.saveAgentConfig({
        upsellSettings: {
          enabled: upsellEnabled,
          style: upsellStyle,
          maxAttempts: upsellMaxAttempts,
          skipOnMultipleItems: upsellSkipOnMultipleItems,
          customUpsellMessage: customUpsellMessage || undefined,
        },
      });
      setSnackbar({ open: true, message: 'Upsell settings saved successfully!', severity: 'success' });
    } catch (error: any) {
      console.error('Error saving upsell settings:', error);
      setSnackbar({ open: true, message: error.message || 'Failed to save upsell settings', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleSaveConversationSettings = async () => {
    try {
      setSaving(true);
      await apiService.saveAgentConfig({
        conversationSettings: {
          staleTimeoutMinutes,
          resetOnNewDay,
        },
      });
      setSnackbar({ open: true, message: 'Conversation settings saved successfully!', severity: 'success' });
    } catch (error: any) {
      console.error('Error saving conversation settings:', error);
      setSnackbar({ open: true, message: error.message || 'Failed to save conversation settings', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleSavePayoutSettings = async () => {
    try {
      setSaving(true);
      await apiService.savePayoutConfig({
        method: payoutMethod,
        bankAccountName: payoutMethod === 'bank' ? bankAccountName : undefined,
        bankAccountNumber: payoutMethod === 'bank' ? bankAccountNumber : undefined,
        bankRoutingNumber: payoutMethod === 'bank' ? bankRoutingNumber : undefined,
      });
      setSnackbar({ open: true, message: 'Payout settings saved successfully!', severity: 'success' });
    } catch (error: any) {
      console.error('Error saving payout settings:', error);
      setSnackbar({ open: true, message: error.message || 'Failed to save payout settings', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        sx={{
          background: 'linear-gradient(180deg, #0a0e27 0%, #1a1f3a 50%, #0a0e27 100%)',
        }}
      >
        <MagicBackground />
        <CircularProgress sx={{ color: '#667eea', position: 'relative', zIndex: 1 }} size={60} />
      </Box>
    );
  }

  return (
    <Box sx={{ 
      background: 'linear-gradient(180deg, #0a0e27 0%, #1a1f3a 50%, #0a0e27 100%)',
      minHeight: '100vh', 
      position: 'relative',
      py: 4 
    }}>
      <MagicBackground />
      
      <Box maxWidth="lg" sx={{ mx: 'auto', px: 3, position: 'relative', zIndex: 1 }}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.5 }}
        >
          <Box mb={4}>
            <Box display="flex" alignItems="center" gap={2} mb={1}>
              <SettingsIcon sx={{ fontSize: 40, color: '#667eea' }} />
              <Typography 
                variant="h3" 
                fontWeight="bold" 
                sx={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                Settings
              </Typography>
            </Box>
            <Typography variant="h6" color="rgba(255,255,255,0.7)">
              Manage your business integrations and preferences
            </Typography>
          </Box>

          {/* Settings Cards Grid */}
          <Grid container spacing={3}>
            {/* Communication & Phone Card */}
            <Grid item xs={12}>
            <Card
              sx={{
                background: 'rgba(255, 255, 255, 0.05)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: 3,
                transition: 'all 0.3s ease',
                '&:hover': {
                  borderColor: 'rgba(102, 126, 234, 0.3)',
                  boxShadow: '0 8px 32px rgba(102, 126, 234, 0.1)',
                },
              }}
            >
              <CardContent sx={{ p: 4 }}>
                <Box display="flex" alignItems="center" gap={2} mb={3}>
                  <PhoneIcon sx={{ fontSize: 32, color: '#667eea' }} />
                  <Box>
                    <Typography variant="h5" fontWeight="bold" color="white">
                      Communication & Phone
                    </Typography>
                    <Typography variant="body2" color="rgba(255,255,255,0.7)">
                      Manage your phone number and SMS settings
                    </Typography>
                  </Box>
                </Box>
                <Divider sx={{ mb: 3, borderColor: 'rgba(255, 255, 255, 0.1)' }} />

                {/* Business Type Selector */}
                <Box mb={4}>
                  <Typography variant="h6" fontWeight="bold" color="white" gutterBottom>
                    Business Type
                  </Typography>
                  <Typography variant="body2" color="rgba(255,255,255,0.7)" mb={2}>
                    Select your business type to customize AI behavior. Appointment-based businesses focus on booking, 
                    order-based focus on menu/orders, and hybrid handles both.
                  </Typography>
                  <TextField
                    fullWidth
                    select
                    label="Business Type"
                    value={businessType}
                    onChange={(e) => setBusinessType(e.target.value as 'appointment' | 'order' | 'hybrid')}
                    SelectProps={{ native: true }}
                    sx={{
                      mb: 2,
                      '& .MuiOutlinedInput-root': {
                        color: 'white',
                        '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                      },
                      '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
                    }}
                  >
                    <option value="order">Order-Based (Restaurants, Coffee Shops, Retail)</option>
                    <option value="appointment">Appointment-Based (Barbers, Spas, Salons)</option>
                    <option value="hybrid">Hybrid (Both Orders & Appointments)</option>
                  </TextField>
                  <Button
                    variant="contained"
                    onClick={async () => {
                      try {
                        setSaving(true);
                        await apiService.updateVendor({ businessType });
                        setSnackbar({ open: true, message: 'Business type saved successfully!', severity: 'success' });
                      } catch (error: any) {
                        setSnackbar({ open: true, message: error.message || 'Failed to save business type', severity: 'error' });
                      } finally {
                        setSaving(false);
                      }
                    }}
                    disabled={saving}
                    sx={{
                      bgcolor: '#667eea',
                      '&:hover': { bgcolor: '#5568d3' },
                    }}
                  >
                    {saving ? <CircularProgress size={24} /> : 'Save Business Type'}
                  </Button>
                </Box>
                <Divider sx={{ mb: 3, borderColor: 'rgba(255, 255, 255, 0.1)' }} />

                {/* Business Address */}
                <Box mb={4}>
                  <Typography variant="h6" fontWeight="bold" color="white" gutterBottom>
                    Business Address
                  </Typography>
                  <Typography variant="body2" color="rgba(255,255,255,0.7)" mb={2}>
                    Your business address will be included in order confirmations for pickup orders.
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
                    <TextField
                      fullWidth
                      label="Street Address"
                      value={addressStreet}
                      onChange={(e) => setAddressStreet(e.target.value)}
                      placeholder="123 Main St"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          color: 'white',
                          '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                          '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                          '&.Mui-focused fieldset': { borderColor: '#667eea' },
                        },
                        '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
                      }}
                    />
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <TextField
                        fullWidth
                        label="City"
                        value={addressCity}
                        onChange={(e) => setAddressCity(e.target.value)}
                        placeholder="Aurora"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            color: 'white',
                            '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                            '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                            '&.Mui-focused fieldset': { borderColor: '#667eea' },
                          },
                          '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
                        }}
                      />
                      <TextField
                        select
                        label="State"
                        value={addressState}
                        onChange={(e) => setAddressState(e.target.value)}
                        sx={{
                          minWidth: 110,
                          '& .MuiOutlinedInput-root': {
                            color: 'white',
                            '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                            '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                            '&.Mui-focused fieldset': { borderColor: '#667eea' },
                          },
                          '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
                          '& .MuiSvgIcon-root': { color: 'rgba(255,255,255,0.7)' },
                        }}
                      >
                        <MenuItem value=""><em>—</em></MenuItem>
                        {US_STATES.map((s) => <MenuItem key={s} value={s} sx={{ color: 'inherit' }}>{s}</MenuItem>)}
                      </TextField>
                      <TextField
                        label="ZIP"
                        value={addressZip}
                        onChange={(e) => setAddressZip(e.target.value)}
                        placeholder="80015"
                        inputProps={{ maxLength: 10 }}
                        sx={{
                          width: 120,
                          '& .MuiOutlinedInput-root': {
                            color: 'white',
                            '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                            '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                            '&.Mui-focused fieldset': { borderColor: '#667eea' },
                          },
                          '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
                        }}
                      />
                    </Box>
                  </Box>
                  <Button
                    variant="contained"
                    onClick={async () => {
                      try {
                        setSaving(true);
                        const parts = [
                          addressStreet.trim(),
                          addressCity.trim(),
                          [addressState.trim(), addressZip.trim()].filter(Boolean).join(' '),
                        ].filter(Boolean);
                        const fullAddress = parts.length > 0 ? parts.join(', ') : undefined;
                        await apiService.updateVendor({ address: fullAddress });
                        setSnackbar({ open: true, message: 'Business address saved successfully!', severity: 'success' });
                      } catch (error: any) {
                        setSnackbar({ open: true, message: error.message || 'Failed to save business address', severity: 'error' });
                      } finally {
                        setSaving(false);
                      }
                    }}
                    disabled={saving}
                    sx={{
                      bgcolor: '#667eea',
                      '&:hover': { bgcolor: '#5568d3' },
                    }}
                  >
                    {saving ? <CircularProgress size={24} /> : 'Save Address'}
                  </Button>
                </Box>
                <Divider sx={{ mb: 3, borderColor: 'rgba(255, 255, 255, 0.1)' }} />

                {/* Employee Queue Access */}
                <Box mb={4}>
                  <Typography variant="h6" fontWeight="bold" color="white" gutterBottom>
                    Employee Queue Access
                  </Typography>
                  <Typography variant="body2" color="rgba(255,255,255,0.7)" mb={2}>
                    Generate a PIN for employees to access the queue view. They can use this to view and manage orders and appointments without full dashboard access.
                  </Typography>
                  
                  {queueAccessUrl ? (
                    <Box mb={2} p={2} sx={{ bgcolor: 'rgba(102, 126, 234, 0.1)', borderRadius: 2, border: '1px solid rgba(102, 126, 234, 0.2)' }}>
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                        <Typography variant="body2" color="rgba(255,255,255,0.9)" fontWeight="bold">
                          Queue Access URL:
                        </Typography>
                        <Box display="flex" gap={1}>
                          <IconButton
                            size="small"
                            onClick={() => {
                              navigator.clipboard.writeText(queueAccessUrl);
                              setSnackbar({ open: true, message: 'URL copied to clipboard!', severity: 'success' });
                            }}
                            sx={{ color: '#667eea' }}
                          >
                            <ContentCopyIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => window.open(queueAccessUrl, '_blank')}
                            sx={{ color: '#667eea' }}
                          >
                            <OpenInNewIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </Box>
                      <Typography variant="body2" color="#667eea" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                        {queueAccessUrl}
                      </Typography>
                      <Typography variant="caption" color="rgba(255,255,255,0.6)" mt={1} display="block">
                        Share this URL with employees. They'll need to enter the PIN to access the queue.
                      </Typography>
                    </Box>
                  ) : (
                    <Box mb={2} p={2} sx={{ bgcolor: 'rgba(255, 255, 255, 0.05)', borderRadius: 2, border: '1px solid rgba(255, 255, 255, 0.1)' }}>
                      <Typography variant="body2" color="rgba(255,255,255,0.7)">
                        Queue access URL will be generated once you save your business settings.
                      </Typography>
                    </Box>
                  )}
                  
                  {vendor?.vendorId && (
                    <Box mb={2}>
                      <Button
                        variant="outlined"
                        startIcon={<OpenInNewIcon />}
                        onClick={() => navigate('/queue')}
                        sx={{
                          color: 'white',
                          borderColor: 'rgba(102, 126, 234, 0.5)',
                          '&:hover': { borderColor: '#667eea', bgcolor: 'rgba(102, 126, 234, 0.1)' },
                        }}
                      >
                        View Queue (Owner Access)
                      </Button>
                    </Box>
                  )}
                  
                  <TextField
                    fullWidth
                    label="Employee PIN (4-6 digits)"
                    value={employeePin}
                    onChange={(e) => setEmployeePin(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    type={showEmployeePin ? 'text' : 'password'}
                    placeholder="Enter PIN"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowEmployeePin(!showEmployeePin)}
                            edge="end"
                            sx={{ color: 'rgba(255,255,255,0.7)' }}
                          >
                            {showEmployeePin ? <VisibilityOffIcon /> : <VisibilityIcon />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      mb: 2,
                      '& .MuiOutlinedInput-root': {
                        color: 'white',
                        '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                        '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                        '&.Mui-focused fieldset': { borderColor: '#667eea' },
                      },
                      '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
                    }}
                  />
                  <Box display="flex" gap={2}>
                    <Button
                      variant="outlined"
                      onClick={() => {
                        // Generate random 4-digit PIN
                        const pin = Math.floor(1000 + Math.random() * 9000).toString();
                        setEmployeePin(pin);
                        setShowEmployeePin(true);
                      }}
                      sx={{
                        color: 'white',
                        borderColor: 'rgba(255, 255, 255, 0.3)',
                        '&:hover': { borderColor: '#667eea', bgcolor: 'rgba(102, 126, 234, 0.1)' },
                      }}
                    >
                      Generate PIN
                    </Button>
                    <Button
                      variant="contained"
                      onClick={async () => {
                        if (!employeePin || employeePin.length < 4) {
                          setSnackbar({ open: true, message: 'PIN must be at least 4 digits', severity: 'error' });
                          return;
                        }
                        try {
                          setSaving(true);
                          // Store PIN (in production, this should be hashed on backend)
                          await apiService.updateVendor({ employeePin: employeePin });
                          setSnackbar({ open: true, message: 'Employee PIN saved successfully!', severity: 'success' });
                          setShowEmployeePin(false);
                        } catch (error: any) {
                          setSnackbar({ open: true, message: error.message || 'Failed to save employee PIN', severity: 'error' });
                        } finally {
                          setSaving(false);
                        }
                      }}
                      disabled={saving || !employeePin}
                      sx={{
                        bgcolor: '#667eea',
                        '&:hover': { bgcolor: '#5568d3' },
                      }}
                    >
                      {saving ? <CircularProgress size={24} /> : 'Save PIN'}
                    </Button>
                  </Box>
                </Box>
                <Divider sx={{ mb: 3, borderColor: 'rgba(255, 255, 255, 0.1)' }} />

                <Typography variant="h6" fontWeight="bold" color="white" gutterBottom>
                  Your Phone Number
                </Typography>
              <Typography variant="body2" color="rgba(255,255,255,0.7)" mb={3}>
                Every business automatically gets its own dedicated phone number. Customers can text this number to chat with your AI agent.
              </Typography>

              {/* Display current phone number */}
              {vendor?.phone ? (
                <Box mt={3} p={3} sx={{ 
                  bgcolor: 'rgba(0, 212, 255, 0.1)', 
                  borderRadius: 2,
                  border: '1px solid rgba(0, 212, 255, 0.2)',
                }}>
                  <Box display="flex" alignItems="center" gap={1} mb={2}>
                    <PhoneIcon sx={{ color: '#00D4FF' }} />
                    <Typography variant="body2" color="#00D4FF" fontWeight="bold">
                      Your Phone Number
                    </Typography>
                  </Box>
                  <Typography variant="h5" color="white" sx={{ fontFamily: 'monospace', mb: 2 }}>
                    {vendor.phone}
                  </Typography>
                  
                  {/* Phone Number Status */}
                  {phoneStatus && (
                    <Box mb={2}>
                      {phoneStatus.status === 'ACTIVE' ? (
                        <Alert 
                          severity="success" 
                          sx={{ 
                            bgcolor: 'rgba(67, 233, 123, 0.15)', 
                            border: '1px solid rgba(67, 233, 123, 0.3)',
                            '& .MuiAlert-icon': { color: '#43e97b' }
                          }}
                          icon={<CheckCircleOutlineIcon />}
                        >
                          <Typography variant="body2" color="white" fontWeight="bold">
                            ✅ Phone Number Active
                          </Typography>
                          <Typography variant="body2" color="rgba(255,255,255,0.8)" sx={{ mt: 0.5 }}>
                            {phoneStatus.message}
                          </Typography>
                        </Alert>
                      ) : phoneStatus.status === 'PENDING' ? (
                        <Alert 
                          severity="warning" 
                          sx={{ 
                            bgcolor: 'rgba(255, 193, 7, 0.15)', 
                            border: '1px solid rgba(255, 193, 7, 0.3)',
                            '& .MuiAlert-icon': { color: '#FFC107' }
                          }}
                          icon={<HourglassEmptyIcon />}
                        >
                          <Typography variant="body2" color="white" fontWeight="bold">
                            ⏳ Phone Number Pending
                          </Typography>
                          <Typography variant="body2" color="rgba(255,255,255,0.8)" sx={{ mt: 0.5 }}>
                            {phoneStatus.message}
                          </Typography>
                        </Alert>
                      ) : (
                        <Alert 
                          severity="info" 
                          sx={{ 
                            bgcolor: 'rgba(0, 212, 255, 0.1)', 
                            border: '1px solid rgba(0, 212, 255, 0.2)',
                          }}
                        >
                          <Typography variant="body2" color="white">
                            Status: {phoneStatus.status} - {phoneStatus.message}
                          </Typography>
                        </Alert>
                      )}
                    </Box>
                  )}

                  <Alert severity="info" sx={{ bgcolor: 'rgba(0, 212, 255, 0.1)', border: 'none', mb: 2 }}>
                    <Typography variant="body2" color="white">
                      <strong>How to use:</strong> Customers can text this number to chat with your AI agent. 
                      The agent will automatically respond based on your business configuration.
                    </Typography>
                  </Alert>
                  
                  {/* Spending Data */}
                  {spendingData && (
                    <Box mt={2} p={2} sx={{ bgcolor: 'rgba(0, 0, 0, 0.2)', borderRadius: 1 }}>
                      <Typography variant="subtitle2" color="white" fontWeight="bold" mb={1}>
                        SMS Spending (This Month)
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="rgba(255,255,255,0.7)">Messages Sent</Typography>
                          <Typography variant="body2" color="white">{spendingData.messagesSent}</Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="rgba(255,255,255,0.7)">Messages Received</Typography>
                          <Typography variant="body2" color="white">{spendingData.messagesReceived}</Typography>
                        </Grid>
                        <Grid item xs={12}>
                          <Typography variant="caption" color="rgba(255,255,255,0.7)">Total Spending</Typography>
                          <Typography variant="h6" color="#00D4FF">${spendingData.spending.total.toFixed(2)}</Typography>
                        </Grid>
                        {spendingLimit && (
                          <Grid item xs={12}>
                            <Typography variant="caption" color="rgba(255,255,255,0.7)">Spending Limit</Typography>
                            <Typography variant="body2" color="white">${spendingLimit.toFixed(2)}/month</Typography>
                          </Grid>
                        )}
                      </Grid>
                    </Box>
                  )}

                  {/* Spending Limit Setting */}
                  <Box mt={3} p={3} sx={{ 
                    bgcolor: 'rgba(102, 126, 234, 0.15)', 
                    borderRadius: 2,
                    border: '2px solid rgba(102, 126, 234, 0.3)',
                  }}>
                    <Typography variant="h6" color="white" fontWeight="bold" mb={1}>
                      💰 Set Monthly Spending Limit
                    </Typography>
                    <Typography variant="body2" color="rgba(255,255,255,0.7)" mb={2}>
                      Control your SMS costs by setting a monthly spending limit. Once reached, SMS sending will be paused until the next month.
                    </Typography>
                    <Box display="flex" gap={2} alignItems="flex-end" flexWrap="wrap">
                      <Box sx={{ flex: 1, minWidth: 200 }}>
                        <Typography variant="caption" color="rgba(255,255,255,0.7)" mb={0.5} display="block">
                          Monthly Limit ($)
                        </Typography>
                        <TextField
                          type="number"
                          fullWidth
                          placeholder={spendingLimit ? spendingLimit.toString() : "50.00"}
                          value={newSpendingLimit}
                          onChange={(e) => setNewSpendingLimit(e.target.value)}
                          InputProps={{
                            startAdornment: <InputAdornment position="start" sx={{ color: 'rgba(255,255,255,0.7)' }}>$</InputAdornment>,
                          }}
                          sx={{ 
                            '& .MuiOutlinedInput-root': {
                              color: 'white',
                              '& fieldset': { borderColor: 'rgba(102, 126, 234, 0.5)' },
                              '&:hover fieldset': { borderColor: 'rgba(102, 126, 234, 0.7)' },
                              '&.Mui-focused fieldset': { borderColor: '#667eea' },
                            },
                          }}
                        />
                      </Box>
                      <Button
                        variant="contained"
                        onClick={handleSetSpendingLimit}
                        disabled={saving || !newSpendingLimit}
                        sx={{ 
                          bgcolor: '#667eea',
                          '&:hover': { bgcolor: '#5568d3' },
                          minWidth: 120,
                          height: 56,
                        }}
                      >
                        {saving ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 'Set Limit'}
                      </Button>
                    </Box>
                    {spendingLimit && (
                      <Box mt={2} p={1.5} sx={{ bgcolor: 'rgba(0, 0, 0, 0.2)', borderRadius: 1 }}>
                        <Typography variant="body2" color="rgba(255,255,255,0.7)">
                          Current limit: <strong style={{ color: '#00D4FF' }}>${spendingLimit.toFixed(2)}/month</strong>
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Box>
              ) : (
                <Box mt={3} p={3} sx={{ 
                  bgcolor: 'rgba(255, 193, 7, 0.1)', 
                  borderRadius: 2,
                  border: '1px solid rgba(255, 193, 7, 0.2)',
                }}>
                  <Typography variant="body2" color="#FFC107" fontWeight="bold" mb={1}>
                    Phone Number Not Activated
                  </Typography>
                  <Typography variant="body2" color="rgba(255,255,255,0.7)" mb={3}>
                    Activate your phone number to enable SMS conversations with customers. 
                    This will provision a dedicated phone number for your business.
                  </Typography>
                  <Button
                    variant="contained"
                    onClick={handleActivatePhone}
                    disabled={saving}
                    sx={{
                      bgcolor: '#00D4FF',
                      '&:hover': { bgcolor: '#00B8E6' },
                    }}
                  >
                    {saving ? <CircularProgress size={24} /> : 'Activate Phone Number'}
                  </Button>
                </Box>
              )}
            </CardContent>
          </Card>
          </Grid>

          {/* Payment & Payout Card */}
          <Grid item xs={12}>
            <Card
              sx={{
                background: 'rgba(255, 255, 255, 0.05)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: 3,
                transition: 'all 0.3s ease',
                '&:hover': {
                  borderColor: 'rgba(102, 126, 234, 0.3)',
                  boxShadow: '0 8px 32px rgba(102, 126, 234, 0.1)',
                },
              }}
            >
              <CardContent sx={{ p: 4 }}>
                <Box display="flex" alignItems="center" gap={2} mb={3}>
                  <PaymentIcon sx={{ fontSize: 32, color: '#667eea' }} />
                  <Box>
                    <Typography variant="h5" fontWeight="bold" color="white">
                      Payment & Payout
                    </Typography>
                    <Typography variant="body2" color="rgba(255,255,255,0.7)">
                      Configure payment providers and payout methods
                    </Typography>
                  </Box>
                </Box>
                <Divider sx={{ mb: 3, borderColor: 'rgba(255, 255, 255, 0.1)' }} />

                {/* Payment Integrations Section */}
                <Box mb={4}>
                  <Typography variant="h6" fontWeight="bold" color="white" gutterBottom>
                    Payment Provider Integrations
                  </Typography>
                  <Typography variant="body2" color="rgba(255,255,255,0.7)" mb={3}>
                    Connect your payment providers to accept payments through your AI agent.
                  </Typography>

                  {/* Default Payment Provider Selection */}
                  {(squareConnected || stripeConnected) && (
                    <Box 
                      mb={3} 
                      p={3} 
                      sx={{ 
                        background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)', 
                        borderRadius: 3, 
                        border: '1px solid rgba(102, 126, 234, 0.2)',
                        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
                        transition: 'transform 0.2s ease-in-out',
                        '&:hover': {
                          border: '1px solid rgba(102, 126, 234, 0.4)',
                          boxShadow: '0 8px 30px rgba(0, 0, 0, 0.15)',
                        }
                      }}
                    >
                      <Box display="flex" alignItems="center" gap={1.5} mb={2}>
                        <ShoppingCartIcon sx={{ color: '#00D4FF' }} />
                        <Typography variant="h6" color="white" fontWeight="bold">
                          Default Payment Provider
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="rgba(255,255,255,0.7)" mb={3}>
                        Select which payment system your AI agent should use for processing orders and payments.
                      </Typography>
                      <Box display="flex" gap={2} flexWrap="wrap">
                        {squareConnected && (
                          <Button
                            variant={vendor?.paymentProcessor?.defaultProvider === 'square' ? 'contained' : 'outlined'}
                            onClick={async () => {
                              try {
                                await apiService.updateVendor({ 
                                  paymentProcessor: { 
                                    ...vendor?.paymentProcessor,
                                    defaultProvider: 'square' 
                                  } 
                                });
                                setSnackbar({ open: true, message: 'Square set as default payment provider', severity: 'success' });
                                loadSettings();
                              } catch (error: any) {
                                setSnackbar({ open: true, message: error.message || 'Failed to update', severity: 'error' });
                              }
                            }}
                            sx={{
                              flex: 1,
                              minWidth: '140px',
                              py: 1.5,
                              borderRadius: 2,
                              color: 'white',
                              borderColor: 'rgba(255, 255, 255, 0.2)',
                              borderWidth: vendor?.paymentProcessor?.defaultProvider === 'square' ? 0 : 1,
                              background: vendor?.paymentProcessor?.defaultProvider === 'square' 
                                ? 'linear-gradient(135deg, #00D4FF 0%, #0066FF 100%)' 
                                : 'rgba(255, 255, 255, 0.05)',
                              boxShadow: vendor?.paymentProcessor?.defaultProvider === 'square'
                                ? '0 4px 15px rgba(0, 212, 255, 0.3)'
                                : 'none',
                              fontWeight: 600,
                              textTransform: 'none',
                              fontSize: '1rem',
                              '&:hover': { 
                                borderColor: '#00D4FF', 
                                background: vendor?.paymentProcessor?.defaultProvider === 'square'
                                  ? 'linear-gradient(135deg, #00B8E6 0%, #0052CC 100%)'
                                  : 'rgba(0, 212, 255, 0.1)',
                                borderWidth: vendor?.paymentProcessor?.defaultProvider === 'square' ? 0 : 1,
                              },
                            }}
                          >
                            Square {vendor?.paymentProcessor?.defaultProvider === 'square' && '✓'}
                          </Button>
                        )}
                        {stripeConnected && (
                          <Button
                            variant={vendor?.paymentProcessor?.defaultProvider === 'stripe' || !vendor?.paymentProcessor?.defaultProvider ? 'contained' : 'outlined'}
                            onClick={async () => {
                              try {
                                await apiService.updateVendor({ 
                                  paymentProcessor: { 
                                    ...vendor?.paymentProcessor,
                                    defaultProvider: 'stripe' 
                                  } 
                                });
                                setSnackbar({ open: true, message: 'Stripe set as default payment provider', severity: 'success' });
                                loadSettings();
                              } catch (error: any) {
                                setSnackbar({ open: true, message: error.message || 'Failed to update', severity: 'error' });
                              }
                            }}
                            sx={{
                              flex: 1,
                              minWidth: '140px',
                              py: 1.5,
                              borderRadius: 2,
                              color: 'white',
                              borderColor: 'rgba(255, 255, 255, 0.2)',
                              borderWidth: vendor?.paymentProcessor?.defaultProvider === 'stripe' || !vendor?.paymentProcessor?.defaultProvider ? 0 : 1,
                              background: vendor?.paymentProcessor?.defaultProvider === 'stripe' || !vendor?.paymentProcessor?.defaultProvider
                                ? 'linear-gradient(135deg, #7B61FF 0%, #667eea 100%)'
                                : 'rgba(255, 255, 255, 0.05)',
                              boxShadow: vendor?.paymentProcessor?.defaultProvider === 'stripe' || !vendor?.paymentProcessor?.defaultProvider
                                ? '0 4px 15px rgba(123, 97, 255, 0.3)'
                                : 'none',
                              fontWeight: 600,
                              textTransform: 'none',
                              fontSize: '1rem',
                              '&:hover': { 
                                borderColor: '#7B61FF', 
                                background: vendor?.paymentProcessor?.defaultProvider === 'stripe' || !vendor?.paymentProcessor?.defaultProvider
                                  ? 'linear-gradient(135deg, #6A50EE 0%, #5568d3 100%)'
                                  : 'rgba(123, 97, 255, 0.1)',
                                borderWidth: vendor?.paymentProcessor?.defaultProvider === 'stripe' || !vendor?.paymentProcessor?.defaultProvider ? 0 : 1,
                              },
                            }}
                          >
                            Stripe {(vendor?.paymentProcessor?.defaultProvider === 'stripe' || !vendor?.paymentProcessor?.defaultProvider) && '✓'}
                          </Button>
                        )}
                      </Box>
                    </Box>
                  )}

                  <Grid container spacing={3}>
                {/* Square Integration */}
                <Grid item xs={12}>
                  <Card
                    sx={{
                      background: 'rgba(255, 255, 255, 0.03)',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      borderRadius: 3,
                      p: 3,
                      transition: 'transform 0.2s, box-shadow 0.2s',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                      }
                    }}
                  >
                    <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
                      <Box display="flex" alignItems="center" gap={2}>
                        <Box
                          sx={{
                            width: 56,
                            height: 56,
                            borderRadius: 3,
                            background: 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)',
                            border: '1px solid rgba(255,255,255,0.1)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                          }}
                        >
                          <AccountBalanceIcon sx={{ color: '#00D4FF', fontSize: 32 }} />
                        </Box>
                        <Box>
                          <Typography variant="h6" fontWeight="bold" color="white">
                            Square
                          </Typography>
                          <Typography variant="body2" color="rgba(255,255,255,0.6)">
                            Sync menu, orders, and payments
                          </Typography>
                        </Box>
                      </Box>
                      {squareConnected ? (
                        <Box 
                          sx={{
                            px: 2,
                            py: 0.75,
                            borderRadius: 10,
                            bgcolor: 'rgba(67, 233, 123, 0.15)',
                            border: '1px solid rgba(67, 233, 123, 0.3)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                          }}
                        >
                          <CheckCircleIcon sx={{ color: '#43e97b', fontSize: 20 }} />
                          <Typography variant="subtitle2" fontWeight={700} color="#43e97b">Connected</Typography>
                        </Box>
                      ) : (
                        <Box 
                          sx={{
                            px: 2,
                            py: 0.75,
                            borderRadius: 10,
                            bgcolor: 'rgba(255, 255, 255, 0.05)',
                            border: '1px solid rgba(255, 255, 255, 0.1)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                          }}
                        >
                          <ErrorIcon sx={{ color: 'rgba(255,255,255,0.4)', fontSize: 20 }} />
                          <Typography variant="subtitle2" color="rgba(255,255,255,0.6)">Not Connected</Typography>
                        </Box>
                      )}
                    </Box>

                    <Typography variant="body2" color="rgba(255,255,255,0.7)" mb={3} sx={{ lineHeight: 1.6 }}>
                      {squareConnected 
                        ? 'Your Square account is successfully connected. Orders placed through your AI agent will automatically sync to your Square POS, and payments will be processed directly to your Square account.'
                        : 'Connect your Square account to automatically sync your menu, process payments, and manage orders directly in your Square Dashboard.'}
                    </Typography>
                    
                    <Box display="flex" justifyContent="flex-end">
                      <Button
                        variant="contained"
                        onClick={handleConnectSquare}
                        disabled={saving || squareConnected}
                        size="large"
                        startIcon={!saving && !squareConnected && <OpenInNewIcon />}
                        sx={{
                          py: 1.5,
                          px: 4,
                          borderRadius: 2,
                          textTransform: 'none',
                          fontWeight: 700,
                          fontSize: '1rem',
                          background: squareConnected 
                            ? 'rgba(67, 233, 123, 0.1)' 
                            : 'linear-gradient(135deg, #00D4FF 0%, #0066FF 100%)',
                          color: squareConnected ? '#43e97b' : 'white',
                          boxShadow: squareConnected ? 'none' : '0 4px 15px rgba(0, 212, 255, 0.3)',
                          border: squareConnected ? '1px solid rgba(67, 233, 123, 0.3)' : 'none',
                          '&:hover': { 
                            background: squareConnected 
                              ? 'rgba(67, 233, 123, 0.2)' 
                              : 'linear-gradient(135deg, #00B8E6 0%, #0052CC 100%)',
                            boxShadow: squareConnected ? 'none' : '0 6px 20px rgba(0, 212, 255, 0.4)',
                          },
                          '&.Mui-disabled': {
                            background: squareConnected ? 'rgba(67, 233, 123, 0.1)' : 'rgba(255, 255, 255, 0.1)',
                            color: squareConnected ? '#43e97b' : 'rgba(255, 255, 255, 0.3)',
                            border: squareConnected ? '1px solid rgba(67, 233, 123, 0.3)' : 'none',
                          }
                        }}
                      >
                        {saving ? <CircularProgress size={24} color="inherit" /> : squareConnected ? 'Connected Successfully' : 'Connect with Square'}
                      </Button>
                    </Box>
                  </Card>
                </Grid>

                {/* Stripe Integration */}
                <Grid item xs={12}>
                  <Card
                    sx={{
                      background: 'rgba(255, 255, 255, 0.03)',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      borderRadius: 2,
                      p: 3,
                    }}
                  >
                    <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                      <Box display="flex" alignItems="center" gap={2}>
                        <Box
                          sx={{
                            width: 48,
                            height: 48,
                            borderRadius: 2,
                            bgcolor: 'rgba(0, 0, 0, 0.2)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <PaymentIcon sx={{ color: '#7B61FF' }} />
                        </Box>
                        <Box>
                          <Typography variant="h6" fontWeight="bold" color="white">
                            Stripe
                          </Typography>
                          <Typography variant="body2" color="rgba(255,255,255,0.7)">
                            Accept payments via Stripe
                          </Typography>
                        </Box>
                      </Box>
                      {stripeConnected ? (
                        <Box display="flex" alignItems="center" gap={1} color="#43e97b">
                          <CheckCircleIcon />
                          <Typography variant="body2" fontWeight={600}>Connected</Typography>
                        </Box>
                      ) : (
                        <Box display="flex" alignItems="center" gap={1} color="rgba(255,255,255,0.5)">
                          <ErrorIcon />
                          <Typography variant="body2">Not Connected</Typography>
                        </Box>
                      )}
                    </Box>

                    {!stripeConnected && (
                      <>
                        <TextField
                          fullWidth
                          label="Stripe Publishable Key"
                          value={stripePublishableKey}
                          onChange={(e) => setStripePublishableKey(e.target.value)}
                          margin="normal"
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              color: 'white',
                              '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                            },
                            '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
                          }}
                        />
                        <TextField
                          fullWidth
                          label="Stripe Secret Key"
                          type={showStripeSecret ? 'text' : 'password'}
                          value={stripeSecretKey}
                          onChange={(e) => setStripeSecretKey(e.target.value)}
                          margin="normal"
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              color: 'white',
                              '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                            },
                            '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
                          }}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  onClick={() => setShowStripeSecret(!showStripeSecret)}
                                  edge="end"
                                  sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
                                >
                                  {showStripeSecret ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                        />
                        <Box mt={2} display="flex" justifyContent="flex-end">
                          <Button
                            variant="contained"
                            onClick={handleConnectStripe}
                            disabled={saving || !stripePublishableKey || !stripeSecretKey}
                            size="large"
                            startIcon={!saving && !stripeConnected && <OpenInNewIcon />}
                            sx={{
                              py: 1.5,
                              px: 4,
                              borderRadius: 2,
                              textTransform: 'none',
                              fontWeight: 700,
                              fontSize: '1rem',
                              background: stripeConnected 
                                ? 'rgba(67, 233, 123, 0.1)' 
                                : 'linear-gradient(135deg, #7B61FF 0%, #667eea 100%)',
                              color: stripeConnected ? '#43e97b' : 'white',
                              boxShadow: stripeConnected ? 'none' : '0 4px 15px rgba(123, 97, 255, 0.3)',
                              border: stripeConnected ? '1px solid rgba(67, 233, 123, 0.3)' : 'none',
                              '&:hover': { 
                                background: stripeConnected 
                                  ? 'rgba(67, 233, 123, 0.2)' 
                                  : 'linear-gradient(135deg, #6A50EE 0%, #5568d3 100%)',
                                boxShadow: stripeConnected ? 'none' : '0 6px 20px rgba(123, 97, 255, 0.4)',
                              },
                              '&.Mui-disabled': {
                                background: stripeConnected ? 'rgba(67, 233, 123, 0.1)' : 'rgba(255, 255, 255, 0.1)',
                                color: stripeConnected ? '#43e97b' : 'rgba(255, 255, 255, 0.3)',
                                border: stripeConnected ? '1px solid rgba(67, 233, 123, 0.3)' : 'none',
                              }
                            }}
                          >
                            {saving ? <CircularProgress size={24} color="inherit" /> : stripeConnected ? 'Connected Successfully' : 'Connect Stripe'}
                          </Button>
                        </Box>
                      </>
                    )}
                  </Card>
                </Grid>
                  </Grid>
                </Box>

                <Divider sx={{ my: 4, borderColor: 'rgba(255, 255, 255, 0.1)' }} />

                {/* Payout Settings Section */}
                <Box>
                  <Typography variant="h6" fontWeight="bold" color="white" gutterBottom>
                    Payout Configuration
                  </Typography>
                  <Typography variant="body2" color="rgba(255,255,255,0.7)" mb={3}>
                    Configure how you receive payouts from payments processed through the platform.
                  </Typography>

              <TextField
                fullWidth
                select
                label="Payout Method"
                value={payoutMethod}
                onChange={(e) => setPayoutMethod(e.target.value as 'stripe' | 'square' | 'bank')}
                margin="normal"
                SelectProps={{
                  native: true,
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: 'white',
                    '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                  },
                  '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
                }}
              >
                <option value="stripe">Stripe Connect</option>
                <option value="square">Square</option>
                <option value="bank">Bank Transfer</option>
              </TextField>

              {payoutMethod === 'bank' && (
                <>
                  <TextField
                    fullWidth
                    label="Account Holder Name"
                    value={bankAccountName}
                    onChange={(e) => setBankAccountName(e.target.value)}
                    margin="normal"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        color: 'white',
                        '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                      },
                      '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
                    }}
                  />
                  <TextField
                    fullWidth
                    label="Account Number"
                    type="password"
                    value={bankAccountNumber}
                    onChange={(e) => setBankAccountNumber(e.target.value)}
                    margin="normal"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        color: 'white',
                        '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                      },
                      '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
                    }}
                  />
                  <TextField
                    fullWidth
                    label="Routing Number"
                    value={bankRoutingNumber}
                    onChange={(e) => setBankRoutingNumber(e.target.value)}
                    margin="normal"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        color: 'white',
                        '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                      },
                      '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
                    }}
                  />
                </>
              )}

              <Box mt={4} display="flex" justifyContent="flex-end">
                <Button
                  variant="contained"
                  onClick={handleSavePayoutSettings}
                  disabled={saving}
                  sx={{
                    bgcolor: '#667eea',
                    '&:hover': { bgcolor: '#5568d3' },
                  }}
                >
                  {saving ? <CircularProgress size={24} /> : 'Save Payout Settings'}
                </Button>
              </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Business Operations Card */}
          <Grid item xs={12}>
            <Card
              sx={{
                background: 'rgba(255, 255, 255, 0.05)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: 3,
                transition: 'all 0.3s ease',
                '&:hover': {
                  borderColor: 'rgba(102, 126, 234, 0.3)',
                  boxShadow: '0 8px 32px rgba(102, 126, 234, 0.1)',
                },
              }}
            >
              <CardContent sx={{ p: 4 }}>
                <Box display="flex" alignItems="center" gap={2} mb={3}>
                  <HourglassEmptyIcon sx={{ fontSize: 32, color: '#667eea' }} />
                  <Box>
                    <Typography variant="h5" fontWeight="bold" color="white">
                      Business Operations
                    </Typography>
                    <Typography variant="body2" color="rgba(255,255,255,0.7)">
                      Configure business hours and operational settings
                    </Typography>
                  </Box>
                </Box>
                <Divider sx={{ mb: 3, borderColor: 'rgba(255, 255, 255, 0.1)' }} />

                <Typography variant="h6" fontWeight="bold" color="white" gutterBottom>
                  Business Hours Configuration
                </Typography>
                <Typography variant="body2" color="rgba(255, 255, 255, 0.7)" sx={{ mb: 3 }}>
                  Configure when your business is open. Orders outside business hours will be handled based on your settings.
                </Typography>

              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Box display="flex" alignItems="center" mb={2}>
                    <Typography variant="body1" color="white" sx={{ mr: 2 }}>
                      Enforce Business Hours
                    </Typography>
                    <Switch
                      checked={enforceBusinessHours}
                      onChange={(e) => setEnforceBusinessHours(e.target.checked)}
                      color="primary"
                    />
                  </Box>
                  <Typography variant="body2" color="rgba(255, 255, 255, 0.6)" sx={{ mb: 3 }}>
                    When enabled, orders can only be placed during business hours (unless pre-orders are allowed).
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Timezone"
                    value={timezone}
                    onChange={(e) => setTimezone(e.target.value)}
                    select
                    SelectProps={{ native: true }}
                    sx={{ mb: 2 }}
                  >
                    <option value="America/New_York">Eastern Time (ET)</option>
                    <option value="America/Chicago">Central Time (CT)</option>
                    <option value="America/Denver">Mountain Time (MT)</option>
                    <option value="America/Los_Angeles">Pacific Time (PT)</option>
                    <option value="America/Phoenix">Arizona Time</option>
                    <option value="America/Anchorage">Alaska Time</option>
                    <option value="Pacific/Honolulu">Hawaii Time</option>
                  </TextField>
                </Grid>

                {enforceBusinessHours && (
                  <>
                    <Grid item xs={12}>
                      <Typography variant="subtitle1" color="white" gutterBottom>
                        Weekly Schedule
                      </Typography>
                    </Grid>
                    {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map((day) => {
                      const dayHours = businessHours[day] || { open: '09:00', close: '17:00', isOpen: true };
                      const dayName = day.charAt(0).toUpperCase() + day.slice(1);
                      return (
                        <Grid item xs={12} key={day}>
                          <Box display="flex" alignItems="center" gap={2}>
                            <Typography variant="body1" color="white" sx={{ minWidth: 100 }}>
                              {dayName}
                            </Typography>
                            <Switch
                              checked={dayHours.isOpen}
                              onChange={(e) => {
                                setBusinessHours({
                                  ...businessHours,
                                  [day]: { ...dayHours, isOpen: e.target.checked },
                                });
                              }}
                              color="primary"
                            />
                            {dayHours.isOpen && (
                              <>
                                <TextField
                                  type="time"
                                  label="Open"
                                  value={dayHours.open}
                                  onChange={(e) => {
                                    setBusinessHours({
                                      ...businessHours,
                                      [day]: { ...dayHours, open: e.target.value },
                                    });
                                  }}
                                  InputLabelProps={{ shrink: true }}
                                  sx={{ width: 120 }}
                                />
                                <Typography variant="body1" color="white">-</Typography>
                                <TextField
                                  type="time"
                                  label="Close"
                                  value={dayHours.close}
                                  onChange={(e) => {
                                    setBusinessHours({
                                      ...businessHours,
                                      [day]: { ...dayHours, close: e.target.value },
                                    });
                                  }}
                                  InputLabelProps={{ shrink: true }}
                                  sx={{ width: 120 }}
                                />
                              </>
                            )}
                            {!dayHours.isOpen && (
                              <Typography variant="body2" color="rgba(255, 255, 255, 0.5)">
                                Closed
                              </Typography>
                            )}
                          </Box>
                        </Grid>
                      );
                    })}

                    <Grid item xs={12}>
                      <Box display="flex" alignItems="center" mb={2}>
                        <Typography variant="body1" color="white" sx={{ mr: 2 }}>
                          Allow Pre-Orders
                        </Typography>
                        <Switch
                          checked={allowPreOrders}
                          onChange={(e) => setAllowPreOrders(e.target.checked)}
                          color="primary"
                        />
                      </Box>
                      <Typography variant="body2" color="rgba(255, 255, 255, 0.6)" sx={{ mb: 2 }}>
                        When enabled, customers can place orders for future dates when you're closed.
                      </Typography>
                    </Grid>

                    {allowPreOrders && (
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          type="number"
                          label="Pre-Order Lead Time (hours)"
                          value={preOrderLeadTime}
                          onChange={(e) => setPreOrderLeadTime(parseInt(e.target.value) || 2)}
                          helperText="Minimum hours in advance for pre-orders"
                          sx={{ mb: 2 }}
                        />
                      </Grid>
                    )}

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        multiline
                        rows={3}
                        label="After Hours Message"
                        value={afterHoursMessage}
                        onChange={(e) => setAfterHoursMessage(e.target.value)}
                        placeholder="We're currently closed. Our hours are..."
                        helperText="Custom message shown when customers try to order outside business hours"
                      />
                    </Grid>
                  </>
                )}

                <Grid item xs={12}>
                  <Box display="flex" justifyContent="flex-end">
                    <Button
                      variant="contained"
                      onClick={handleSaveBusinessHours}
                      disabled={saving}
                      sx={{
                        bgcolor: '#667eea',
                        '&:hover': { bgcolor: '#5568d3' },
                      }}
                    >
                      {saving ? <CircularProgress size={24} /> : 'Save Business Hours'}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* AI Agent Behavior Card */}
          <Grid item xs={12}>
            <Card
              sx={{
                background: 'rgba(255, 255, 255, 0.05)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: 3,
                transition: 'all 0.3s ease',
                '&:hover': {
                  borderColor: 'rgba(102, 126, 234, 0.3)',
                  boxShadow: '0 8px 32px rgba(102, 126, 234, 0.1)',
                },
              }}
            >
              <CardContent sx={{ p: 4 }}>
                <Box display="flex" alignItems="center" gap={2} mb={3}>
                  <SettingsIcon sx={{ fontSize: 32, color: '#667eea' }} />
                  <Box>
                    <Typography variant="h5" fontWeight="bold" color="white">
                      AI Agent Behavior
                    </Typography>
                    <Typography variant="body2" color="rgba(255,255,255,0.7)">
                      Configure upsell and conversation management
                    </Typography>
                  </Box>
                </Box>
                <Divider sx={{ mb: 3, borderColor: 'rgba(255, 255, 255, 0.1)' }} />

                {/* Upsell Settings Section */}
                <Box mb={4}>
                  <Box display="flex" alignItems="center" gap={2} mb={2}>
                    <ShoppingCartIcon sx={{ fontSize: 24, color: '#667eea' }} />
                    <Typography variant="h6" fontWeight="bold" color="white">
                      Upsell Configuration
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="rgba(255, 255, 255, 0.7)" sx={{ mb: 3 }}>
                    Configure how the AI offers additional items to customers during ordering.
                  </Typography>

              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Box display="flex" alignItems="center" mb={2}>
                    <Typography variant="body1" color="white" sx={{ mr: 2 }}>
                      Enable Upsell
                    </Typography>
                    <Switch
                      checked={upsellEnabled}
                      onChange={(e) => setUpsellEnabled(e.target.checked)}
                      color="primary"
                    />
                  </Box>
                </Grid>

                {upsellEnabled && (
                  <>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Upsell Style"
                        value={upsellStyle}
                        onChange={(e) => setUpsellStyle(e.target.value as 'natural' | 'aggressive' | 'none')}
                        select
                        SelectProps={{ native: true }}
                        helperText="Natural: 'Would you like anything else?' | Aggressive: Suggests specific items"
                      >
                        <option value="natural">Natural</option>
                        <option value="aggressive">Aggressive</option>
                        <option value="none">None (use custom message only)</option>
                      </TextField>
                    </Grid>

                    {upsellStyle === 'none' && (
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          multiline
                          rows={2}
                          label="Custom Upsell Message"
                          value={customUpsellMessage}
                          onChange={(e) => setCustomUpsellMessage(e.target.value)}
                          placeholder="Would you like to add a pastry?"
                          helperText="Custom message to ask about additional items"
                        />
                      </Grid>
                    )}

                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        type="number"
                        label="Max Upsell Attempts"
                        value={upsellMaxAttempts}
                        onChange={(e) => setUpsellMaxAttempts(parseInt(e.target.value) || 1)}
                        inputProps={{ min: 1, max: 3 }}
                        helperText="Maximum times to ask before proceeding to payment"
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <Box display="flex" alignItems="center">
                        <Typography variant="body1" color="white" sx={{ mr: 2 }}>
                          Skip Upsell When Customer Orders 2+ Items
                        </Typography>
                        <Switch
                          checked={upsellSkipOnMultipleItems}
                          onChange={(e) => setUpsellSkipOnMultipleItems(e.target.checked)}
                          color="primary"
                        />
                      </Box>
                      <Typography variant="body2" color="rgba(255, 255, 255, 0.6)" sx={{ mt: 1 }}>
                        When enabled, skip upsell if customer orders multiple items at once.
                      </Typography>
                    </Grid>
                  </>
                )}

                <Grid item xs={12}>
                  <Box display="flex" justifyContent="flex-end">
                    <Button
                      variant="contained"
                      onClick={handleSaveUpsellSettings}
                      disabled={saving}
                      sx={{
                        bgcolor: '#667eea',
                        '&:hover': { bgcolor: '#5568d3' },
                      }}
                    >
                      {saving ? <CircularProgress size={24} /> : 'Save Upsell Settings'}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
                </Box>

                <Divider sx={{ my: 4, borderColor: 'rgba(255, 255, 255, 0.1)' }} />

                {/* Conversation Settings Section */}
                <Box>
                  <Box display="flex" alignItems="center" gap={2} mb={2}>
                    <ChatIcon sx={{ fontSize: 24, color: '#667eea' }} />
                    <Typography variant="h6" fontWeight="bold" color="white">
                      Conversation Management
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="rgba(255, 255, 255, 0.7)" sx={{ mb: 3 }}>
                    Configure how the AI manages conversation state and context.
                  </Typography>

              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Stale Conversation Timeout (minutes)"
                    value={staleTimeoutMinutes}
                    onChange={(e) => setStaleTimeoutMinutes(parseInt(e.target.value) || 30)}
                    inputProps={{ min: 5, max: 1440 }}
                    helperText="Reset upsell attempts after this period of inactivity"
                  />
                </Grid>

                <Grid item xs={12}>
                  <Box display="flex" alignItems="center">
                    <Typography variant="body1" color="white" sx={{ mr: 2 }}>
                      Reset State Daily
                    </Typography>
                    <Switch
                      checked={resetOnNewDay}
                      onChange={(e) => setResetOnNewDay(e.target.checked)}
                      color="primary"
                    />
                  </Box>
                  <Typography variant="body2" color="rgba(255, 255, 255, 0.6)" sx={{ mt: 1 }}>
                    When enabled, conversation state (upsell attempts, order items) resets at midnight.
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <Box display="flex" justifyContent="flex-end">
                    <Button
                      variant="contained"
                      onClick={handleSaveConversationSettings}
                      disabled={saving}
                      sx={{
                        bgcolor: '#667eea',
                        '&:hover': { bgcolor: '#5568d3' },
                      }}
                    >
                      {saving ? <CircularProgress size={24} /> : 'Save Conversation Settings'}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
        </motion.div>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}


